import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Container,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal
} from "@mui/material";

import { useNavigate, useLocation } from "react-router-dom";
import { useRestaurant } from "../../contexts/RestaurantContext";
import Rangooli from "../../static/Rangooli.png";
import { getImageProps } from "../../utils/imageUtils";
import {
  Menu as MenuIcon,
  Home,
  LocalShipping,
  Event,
  TableRestaurant,
  Phone,
  ShoppingCart,
  Store,
  Person,
  Logout,
  ArrowForward,
} from "@mui/icons-material";
import OutletSelectionModal from "../../components/user/OutletSelectionModal";
import CartDrawer from "../../components/user/CartDrawer";
import LoginModal from "../../components/user/LoginModal";
import { useNotification } from "../../contexts/NotificationContext";
import HomePage from "./HomePage";
import { keyframes } from "@mui/system";

const marquee = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const navColor = {
  bg: "#FFFEF5",
  primary: "#6B2D14",
  primaryHover: "#4A1D0D",
  text: "#2C1810",
  textSecondary: "#8B7355",
  white: "#FFFFFF",
  hover: "#F5E6D3",
  border: "#E5D4C1",
};

// Consistent button styles for all three components
const actionButtonStyles = {
  textTransform: "none !important",
  fontSize: {
    xs: "0.8rem !important",
    sm: "0.9rem !important",
    md: "1rem !important",
    lg: "1rem !important",
    xl: "1.1rem !important",
  },
  fontWeight: "500 !important",
  px: {
    xs: "12px !important",
    sm: "16px !important",
    md: "18px !important",
    lg: "20px !important",
    xl: "22px !important",
  },
  py: {
    xs: "6px !important",
    sm: "8px !important",
    md: "10px !important",
    lg: "10px !important",
    xl: "11px !important",
  },
  borderRadius: "12px !important",
  transition: "all 0.3s ease !important",
  minWidth: { xs: "auto", sm: "100px", md: "110px", lg: "120px" },
  "&:active": {
    transform: "translateY(0) !important",
    transition: "all 0.1s ease !important",
  },
  "& .MuiButton-startIcon": {
    transition: "transform 0.3s ease !important",
    marginRight: {
      xs: "4px !important",
      sm: "6px !important",
      md: "6px !important",
      lg: "8px !important",
    },
  },
  "&:hover .MuiButton-startIcon": {
    transform: "translateX(-2px) !important",
  },
};

const iconStyles = {
  fontSize: {
    xs: "1rem !important",
    sm: "1.2rem !important",
    md: "1.3rem !important",
    lg: "1.3rem !important",
    xl: "1.4rem !important",
  },
  ml:1
};

export default function HeroSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cart,
    restaurant,
    selectedOutlet,
    showOutletSelectionModal,
    handleOutletSelection,
    showOutletSelection,
    closeOutletSelection,
    loginUser,
  } = useRestaurant();
  const [cartOpen, setCartOpen] = useState(false);
  const [actualServiceType, setActualServiceType] = useState("");
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { showSuccess } = useNotification();
  const open = Boolean(anchorEl);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navigationItems = [
    { label: "Home", path: "/", icon: <Home /> },
    { label: "Catering", path: "/catering", icon: <Event /> },
    {
      label: "Reservation",
      path: "/table-reservations",
      icon: <TableRestaurant />,
    },
    { label: "Contact Us", path: "/contact", icon: <Phone /> },
    { label: "Order Online", path: "/delivery", icon: <ArrowForward /> },
  ];

  // Scroll detection for homepage
  useEffect(() => {
    if (location.pathname === "/" || location.pathname.startsWith("/guest/")) {
      const handleScroll = () => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setIsScrolled(scrollTop > 50);
      };

      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [location.pathname]);

  const handleNavigation = (path) => {
    navigate(path);
  };


  const textRef = useRef(null);
  const [scrollDistance, setScrollDistance] = useState(0);

  useEffect(() => {
    if (textRef.current) {
      const textWidth = textRef.current.scrollWidth;
      const containerWidth = 80;
      const overflow = textWidth - containerWidth;
      setScrollDistance(overflow > 0 ? overflow : 0);
    }
  }, [selectedOutlet?.name]);

  const isActivePath = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLoginModalOpen = (e) => {
    if (loginUser?.username) {
      setAnchorEl(e.currentTarget);
    } else {
      setLoginModalOpen(true);
    }
  };

  // Helper function to get styles based on scroll state
  const getNavbarStyles = () => {
    if (location.pathname === "/" || location.pathname.startsWith("/guest/")) {
      return {
      bgcolor: isScrolled ? "#FFFFFF" : "transparent",
      color: isScrolled ? "#680000" : "#FFFFFF",
      borderColor: isScrolled ? "#680000" : "#FFFFFF",
      buttonBg: isScrolled ? "transparent" : "transparent",
      buttonColor: isScrolled ? "#680000" : "#FFFFFF",
      buttonBorder: isScrolled ? "2px solid #680000" : "2px solid #FFFFFF",
      buttonHoverBg: isScrolled ? "#680000" : "#FFFFFF",
      buttonHoverColor: isScrolled ? "#FFFFFF" : "#680000",
    };
    }

     return {
        bgcolor: "#FFFFFF",
        color: "#680000",
        borderColor: "#680000",
        buttonBg: "transparent",
        buttonColor: "#680000",
        buttonBorder: "2px solid #680000",
        buttonHoverBg: "#680000",
        buttonHoverColor: "#FFFFFF",
      };

   
  };

  const navbarStyles = getNavbarStyles();

  // For homepage with hero image
  if (location.pathname === "/" || location.pathname.startsWith("/guest/")) {
    return (
      <>
        <Box
          sx={{
            width: "100%",
            height: { xs: "40vh", sm: "50vh", md: "65vh", lg: "85vh" },
            backgroundImage: `url(${Rangooli})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* DARK OVERLAY */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.45)",
              zIndex: 1,
            }}
          />

          {/* ⭐ NAVBAR ⭐ */}

          <Container
            maxWidth={false} // This makes container full width
            disableGutters // Remove default padding
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bgcolor: navbarStyles.bgcolor, // Background stretches full width
              boxShadow: "none",
              py: 3,
              zIndex: 10,
              transition: "all 0.3s ease",
              borderRadius: "0px !important",
              display: 'flex',
              justifyContent: 'center',
              height: { md: '154px' }, // Or set specific height like '80px'
            }}>
            <AppBar
              position="fixed"
              // elevation={isScrolled ? 2 : 0}
              sx={{
                maxWidth: '1440px',
                margin: '0 auto',
                top: 0,
                left: 0,
                right: 0,
                bgcolor: navbarStyles.bgcolor,
                // boxShadow: isScrolled ? "0 2px 20px rgba(0,0,0,0.1)" : "none",
                boxShadow: "none",
                px: 2,
                py: isScrolled ? 2 : 3,
                zIndex: 10,
                transition: "all 0.3s ease",
                borderRadius: "0px !important",
              }}
            >
              <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
                {/* Mobile Menu Button */}
                <IconButton
                  edge="start"
                  aria-label="menu"
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{
                    display: { xs: "flex", md: "none" },
                    color: navbarStyles.color,
                    p: 1,
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: navbarStyles.buttonHoverBg,
                      color: navbarStyles.buttonHoverColor,
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>

                {/* Logo */}
                <Box
                  sx={{
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/")}
                >
                  {restaurant?.logo_url ? (
                    <Box
                      component="img"
                      src={getImageProps(restaurant.logo_url).src}
                      alt="Logo"
                      sx={{
                        width: { xs: 65, sm: 75, md: 100 },
                        height: { xs: 65, sm: 75, md: 100 },
                        borderRadius: "50%",
                        objectFit: "cover",
                        transition: "all 0.3s ease",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        bgcolor: isScrolled ? "#680000" : "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isScrolled ? "#FFFFFF" : "#680000",
                        fontWeight: "bold",
                        fontSize: "1.2rem",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {restaurant?.name?.charAt(0) || "R"}
                    </Box>
                  )}
                </Box>

                {/* CENTER — MENU ITEMS */}
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    gap: 4,
                    alignItems: "center",
                  }}
                >
                  {navigationItems.map((item) =>
                    item.label !== "Order Online" ? (
                      <Typography
                        key={item.path || item.label}
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          color: navbarStyles.color,
                          fontWeight: isActivePath(item.path)
                            ? "600 !important"
                            : "500 !important",
                          fontSize: {
                            xs: "0.8rem !important",
                            sm: "0.9rem !important",
                            md: "1rem !important",
                            lg: "1.1rem !important",
                            xl: "1.2rem !important",
                          },
                          px: {
                            xs: 1.5,
                            sm: 1.75,
                            md: 2,
                            lg: 2.5,
                          },
                          py: {
                            xs: 1,
                            sm: 1.125,
                            md: 1.25,
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: "8px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          "&:active": {
                            transform: "translateY(0)",
                            transition: "all 0.1s ease",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "50%",
                            width: isActivePath(item.path) ? "80%" : "0%",
                            height: "2px",
                            backgroundColor: navbarStyles.color,
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform: "translateX(-50%)",
                          },
                          "&:hover::before": {
                            width: "80%",
                          },
                        }}
                      >
                        {item.label}
                      </Typography>
                    ) : (
                      <Button
                        variant="contained"
                        endIcon={
                          <ArrowForward
                            sx={{
                              fontSize: {
                                xs: "1rem !important",
                                sm: "1.4rem !important",
                                md: "1.5rem !important",
                                lg: "1.6rem !important",
                              },
                            }}
                          />
                        }
                        onClick={() => navigate("/delivery")}
                        sx={{
                          bgcolor: isScrolled
                            ? "#680000 !important"
                            : "#F9F9F9 !important",
                          color: isScrolled
                            ? "#FFFFFF !important"
                            : "#680000 !important",
                          textTransform: "none !important",
                          fontSize: {
                            xs: "0.8rem !important",
                            sm: "0.9rem !important",
                            md: "1rem !important",
                            lg: "1.1rem !important",
                            xl: "1.2rem !important",
                          },
                          fontWeight: "600 !important",
                          px: {
                            xs: "16px !important",
                            sm: "20px !important",
                            md: "24px !important",
                            lg: "28px !important",
                          },
                          py: {
                            xs: "8px !important",
                            sm: "10px !important",
                            md: "12px !important",
                            lg: "14px !important",
                          },
                          borderRadius: "12px !important",
                          boxShadow: "0 4px 12px rgba(104, 0, 0, 0.2) !important",
                          transition: "all 0.3s ease !important",
                          minWidth: { xs: "80px", sm: "140px", md: "160px" },
                          "& .MuiButton-endIcon": {
                            transition: "transform 0.3s ease !important",
                            marginLeft: {
                              xs: "4px !important",
                              sm: "6px !important",
                              md: "8px !important",
                              lg: "10px !important",
                            },
                          },
                          "&:hover": {
                            bgcolor: isScrolled
                              ? "#4c0000 !important"
                              : "#FFFFFF !important",
                            transform: "translateY(-2px)",
                            boxShadow:
                              "0 6px 20px rgba(104, 0, 0, 0.3) !important",
                          },
                          "&:hover .MuiButton-endIcon": {
                            transform: "translateX(4px) !important",
                          },
                        }}
                      >
                        Order Online
                      </Button>
                    )
                  )}
                </Box>

                {/* RIGHT — ACTION BUTTONS */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {/* Mobile Order Button */}
                  <Button
                    variant="contained"
                    endIcon={
                      <ArrowForward
                        sx={{
                          fontSize: {
                            xs: "1rem !important",
                            sm: "1.4rem !important",
                            md: "1.5rem !important",
                            lg: "1.6rem !important",
                          },
                        }}
                      />
                    }
                    onClick={() => navigate("/delivery")}
                    sx={{
                      display: { xs: "flex", sm: "none" },
                      bgcolor: isScrolled
                        ? "#680000 !important"
                        : "#F9F9F9 !important",
                      color: isScrolled
                        ? "#FFFFFF !important"
                        : "#680000 !important",
                      textTransform: "none !important",
                      fontSize: {
                        xs: "0.8rem !important",
                        sm: "0.9rem !important",
                        md: "1rem !important",
                        lg: "1.1rem !important",
                        xl: "1.2rem !important",
                      },
                      fontWeight: "600 !important",
                      px: {
                        xs: "12px !important",
                        sm: "16px !important",
                        md: "18px !important",
                      },
                      py: {
                        xs: "6px !important",
                        sm: "8px !important",
                        md: "10px !important",
                      },
                      borderRadius: "12px !important",
                      boxShadow: "0 4px 12px rgba(104, 0, 0, 0.2) !important",
                      transition: "all 0.3s ease !important",
                      minWidth: "80px",
                      "&:hover": {
                        bgcolor: isScrolled
                          ? "#4c0000 !important"
                          : "#FFFFFF !important",
                      },
                    }}
                  >
                    Order
                  </Button>

                  {/* Shopping Cart Button */}
                  <Button
                    variant="outlined"
                    startIcon={<ShoppingCart sx={iconStyles} />}
                    onClick={() => setCartOpen(true)}
                    sx={{
                      ...actionButtonStyles,
                      color: `${navbarStyles.buttonColor} !important`,
                      border: `${navbarStyles.buttonBorder} !important`,
                      bgcolor: `${navbarStyles.buttonBg} !important`,
                      "&:hover": {
                        bgcolor: `${navbarStyles.buttonHoverBg} !important`,
                        color: `${navbarStyles.buttonHoverColor} !important`,
                      },
                    }}
                  >
                    <Badge
                      badgeContent={cart.length || 0}
                      color="error"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          minWidth: "16px",
                          height: "16px",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: { xs: "none", sm: "block" },
                          fontSize: "inherit",
                        }}
                      >
                        Cart
                      </Box>
                    </Badge>
                  </Button>

                  {/* Store Button */}
                  {/* <Button
                    variant="outlined"
                    startIcon={<Store sx={iconStyles} />}
                    onClick={showOutletSelection}
                    sx={{
                      ...actionButtonStyles,
                      display: { xs: "none", lg: "flex" },
                      color: `${navbarStyles.buttonColor} !important`,
                      border: `${navbarStyles.buttonBorder} !important`,
                      bgcolor: `${navbarStyles.buttonBg} !important`,
                      "&:hover": {
                        bgcolor: `${navbarStyles.buttonHoverBg} !important`,
                        color: `${navbarStyles.buttonHoverColor} !important`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "120px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        textAlign: "center",
                      }}
                    >
                      {selectedOutlet?.name?.split(" ")[0] || "Stores"}
                    </Box>
                  </Button> */}
                  <Button
                    variant="outlined"
                    startIcon={<Store sx={iconStyles} />}
                    onClick={showOutletSelection}
                    sx={{
                      ...actionButtonStyles,
                      display: { xs: "none", lg: "flex" },
                      color: `${navbarStyles.buttonColor} !important`,
                      border: `${navbarStyles.buttonBorder} !important`,
                      bgcolor: `${navbarStyles.buttonBg} !important`,
                      "&:hover": {
                        bgcolor: `${navbarStyles.buttonHoverBg} !important`,
                        color: `${navbarStyles.buttonHoverColor} !important`,
                      },
                      width: "175px",
                      px: 1,
                      overflow: "hidden",
                      position: "relative",
                      // Ensure button content is properly aligned
                      justifyContent: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        width: "100%",
                        position: "relative",
                        // Account for startIcon space
                        ml: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-block",
                          whiteSpace: "nowrap",
                          animation: `marquee ${Math.max(5, (selectedOutlet?.name?.length ?? 0) * 0.2)}s linear infinite`,
                          width: "auto",
                          "@keyframes marquee": {
                            "0%": {
                              transform: "translateX(0%)",
                            },
                            "100%": {
                              transform: "translateX(-50%)", // Move by half since content is duplicated
                            },
                          },
                        }}
                      >
                        <span style={{ paddingRight: "30px" }}>
                          {selectedOutlet?.name || "Stores"}
                        </span>
                        <span>
                          {selectedOutlet?.name || "Stores"}
                        </span>
                      </Box>
                    </Box>
                  </Button>


                  {/* Login Button */}
                  <Button
                    variant="outlined"
                    startIcon={<Person sx={iconStyles} />}
                    onClick={(e) => handleLoginModalOpen(e)}
                    sx={{
                      ...actionButtonStyles,
                      color: `${navbarStyles.buttonColor} !important`,
                      border: `${navbarStyles.buttonBorder} !important`,
                      bgcolor: `${navbarStyles.buttonBg} !important`,
                      "&:hover": {
                        bgcolor: `${navbarStyles.buttonHoverBg} !important`,
                        color: `${navbarStyles.buttonHoverColor} !important`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100px",
                      }}
                    >
                      {loginUser?.username || "Login"}
                    </Box>
                    {loginUser?.username && (
                      <Box
                        sx={{
                          display: { xs: "flex", sm: "none" },
                          fontSize: "inherit",
                        }}
                      >
                        {loginUser?.username?.charAt(0).toUpperCase()}
                      </Box>
                    )}
                  </Button>
                </Box>
              </Toolbar>
            </AppBar>
          </Container>

          {/* HERO TEXT */}
          <Container
            sx={{ position: "relative", zIndex: 2, textAlign: "center" }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                color: "white",
                fontFamily: "Playfair Display !important",
                fontSize: {
                  xs: "30px !important",
                  sm: "40px !important",
                  md: "90px !important",
                },
              }}
            >
              Welcome to Rangooli
            </Typography>

            <Typography
              sx={{
                color: "white",
                opacity: 0.9,
                fontSize: {
                  xs: "15px !important",
                  sm: "18px !important",
                  md: "24px !important",
                },
                fontFamily: "Playfair Display !important",
                fontWeight: 400,
              }}
            >
              A Fine-Dining Journey Through India's Rich Flavors.
            </Typography>
          </Container>

          {/* MOBILE DRAWER */}
          <Drawer
            anchor="left"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 3 }}
            PaperProps={{
              sx: {
                width: { xs: "75vw", sm: 280 },
                maxWidth: "280px",
                bgcolor: navColor.bg,
                borderRadius: 0,
              },
            }}
          >
            <Box sx={{ p: 2, textAlign: "center" }}>
              {restaurant?.logo_url ? (
                <Box
                  component="img"
                  src={getImageProps(restaurant.logo_url).src}
                  sx={{
                    width: 60,
                    height: 60,
                    mx: "auto",
                    mb: 1,
                    borderRadius: "50%",
                    objectFit: "cover",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    mx: "auto",
                    mb: 1,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #D4AF37 0%, #C19A6B 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                  }}
                >
                  {restaurant?.name?.charAt(0) || "R"}
                </Box>
              )}
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={{ color: navColor.text }}
              >
                {restaurant?.name || "Restaurant"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: navColor.textSecondary, mb: 1 }}
              >
                {restaurant?.tagline || "Delicious food, great service"}
              </Typography>
            </Box>

            <Divider />

            <List sx={{ px: 1, py: 1 }}>
              {navigationItems.map((item) =>
                item.label !== "Order Online" ? (
                  <ListItem key={item.path || item.label} disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.5,
                        py: 1.5,
                        bgcolor: isActivePath(item.path)
                          ? navColor.hover
                          : "transparent",
                        color: isActivePath(item.path)
                          ? navColor.primary
                          : navColor.text,
                        "&:hover": {
                          bgcolor: navColor.hover,
                          color: navColor.primary,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: "inherit",
                          minWidth: 40,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "1rem",
                            fontWeight: isActivePath(item.path) ? 600 : 400,
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<LocalShipping />}
                    onClick={() => {
                      navigate("/delivery");
                      setMobileMenuOpen(false);
                    }}
                    sx={{
                      borderRadius: "12px",
                      padding: "16px 24px",
                      backgroundColor: "#680000",
                      color: "#F9F9F9",
                      fontSize: "12px !important",
                      fontWeight: 600,
                      mt: 1,
                    }}
                  >
                    Order Online
                  </Button>
                )
              )}
            </List>

            <Divider />

            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Store />}
                onClick={() => {
                  showOutletSelection();
                  setMobileMenuOpen(false);
                }}
                sx={{
                  mb: 1.5,
                  py: 1.5,
                  fontSize: "0.9rem",
                  textTransform: "none",
                  borderRadius: 1.5,
                  color: "#680000 !important",
                  border: "2px solid #680000 !important",
                  "&:hover": {
                    color: "#FFFFFF !important",
                    bgcolor: "#680000 !important",
                    border: "1px solid #680000 !important",
                    boxShadow: "0 6px 20px rgba(104, 0, 0, 0.2) !important",
                    transform: "translateY(-2px) !important",
                  },
                }}
              >
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "180px",
                  }}
                >
                  {selectedOutlet?.name || "Select Outlet"}
                </Box>
              </Button>
            </Box>
          </Drawer>

          {/* MODALS */}
          <OutletSelectionModal
            open={showOutletSelectionModal}
            onClose={closeOutletSelection}
            onOutletSelect={handleOutletSelection}
          />
          <CartDrawer
            open={cartOpen}
            onClose={() => setCartOpen(false)}
            actualServiceType={actualServiceType}
            setActualServiceType={setActualServiceType}
          />
          <LoginModal
            loginModalOpen={loginModalOpen}
            setLoginModalOpen={setLoginModalOpen}
          />

          {/* USER MENU */}
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            slotProps={{
              list: {
                "aria-labelledby": "basic-button",
              },
            }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 1.5,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              },
            }}
          >
            <MenuItem
              onClick={() => navigate("/profile")}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": { bgcolor: navColor.hover },
              }}
            >
              <Person style={{ marginRight: 8 }} /> Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                // localStorage.setItem("Hero Section", "YES")
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                localStorage.removeItem("cart_items");
                sessionStorage.removeItem("user");
                setAnchorEl(null);
                navigate("/");
                showSuccess("User Logged Out Successfully");
                window.location.reload();
              }}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": { bgcolor: navColor.hover },
              }}
            >
              <Logout style={{ marginRight: 8 }} /> Logout
            </MenuItem>
          </Menu>
        </Box>
        <HomePage />
      </>
    );
  }

  // For other pages, return null (NormalNavbar will be used instead)
  return null;
}
